import { CommonModule } from '@angular/common';
import { Component, ViewChild, inject, signal } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import {
  AlertComponent,
  ButtonComponent,
  InputLabelComponent,
  MbsPopupType,
  ModalComponent,
  ModalFooterDirective,
  ModalService
} from 'mbs-ui-kit';
import { SelectLicenseParams } from 'mbs-ui-shared@components/components/models/license.model';
import { BuyLicenseModalComponent } from 'mbs-ui-shared@components/licenses/components/buy-license-modal/buy-license-modal.component';
import { ConfirmAssignLicenseWithoutAgentModalComponent } from 'mbs-ui-shared@components/licenses/components/confirm-assign-license-without-agent-modal/confirm-assign-license-without-agent-modal.component';
import { ChangeLicenseCase } from 'mbs-ui-shared@components/licenses/components/select-license-modal/types';
import { SelectLicenseFormComponent } from 'mbs-ui-shared@components/select-license-form/select-license-form.component';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { LicensesFacade } from 'mbs-ui-shared@facades/licenses.facade';
import { HelpLinkDirective } from 'mbs-ui-shared@help/helpLink.directive';
import { LicenseType, PermissionsEnum } from 'mbs-ui-shared@models';
import Computer from 'mbs-ui-shared@models/Computer';
import { ComputerLicensePool, getAgentTypeForLicenseKind } from 'mbs-ui-shared@models/License';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { Observable, noop } from 'rxjs';
import { POST_PAYMENT_TOKEN } from '../../../../tokens/post-payment.token';

@UntilDestroy()
@Component({
  selector: 'mbs-select-license-modal',
  templateUrl: './select-license-modal.component.html',
  styleUrls: ['./select-license-modal.component.scss'],
  imports: [
    CommonModule,
    ButtonComponent,
    HelpLinkDirective,
    SelectLicenseFormComponent,
    I18NextPipe,
    InputLabelComponent,
    ModalComponent,
    ModalFooterDirective,
    AlertComponent
  ],
  standalone: true
})
export class SelectLicenseModalComponent {
  isPostPayment = inject<Observable<boolean>>(POST_PAYMENT_TOKEN);
  private licensesService = inject(LicensesService);
  private modalService = inject(ModalService);
  private licensesFacade = inject(LicensesFacade);
  private ability = inject(AbilityService);

  public readonly mbsPopupType = MbsPopupType;
  private readonly rmmCELicenseTypeId = '00000000-0000-0000-0000-000000000001';

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public setLicenseLoading = signal(false);
  public showRMMPECEAlert = signal(false);

  public form = new FormGroup({
    changeLicenseCase: new FormControl(null),
    selectedLicense: new FormControl(null),
    selectedAnotherPoolLicense: new FormControl(null),
    selectedLicenseType: new FormControl(null)
  });

  public get computer(): Computer {
    return this.baseModal.data.computer;
  }

  public get licenseTypeId() {
    return this.baseModal?.data?.licenseTypeId;
  }

  public get licensePool(): ComputerLicensePool {
    return this.baseModal.data.pool;
  }

  public get mode(): 'backup' | 'rmm' | 'connect' {
    return !['backup', 'rmm', 'connect'].includes(this.baseModal.data?.mode) ? 'backup' : this.baseModal.data?.mode;
  }

  public get computerLicenseByMode() {
    switch (this.mode) {
      case 'rmm':
        return this.computer?.rmmLicense;
      case 'connect':
        return this.computer?.connectLicense;
      default:
        return this.computer?.backupLicense;
    }
  }

  public get isLinux(): boolean {
    return Computer.isLinux(this.computer);
  }

  public get isMac(): boolean {
    return Computer.isMac(this.computer);
  }

  public get isDesktop(): boolean {
    return !this.isServer;
  }

  public get isServer(): boolean {
    return Computer.isServer(this.computer);
  }

  public get hid() {
    return this.baseModal?.data?.computer?.hid;
  }

  public get kind() {
    return this.baseModal?.data?.kind;
  }

  public get isSelectBuyLicense() {
    return this.form.get('changeLicenseCase').value === ChangeLicenseCase.BUY && !this.rmmCESelected;
  }

  private get rmmCESelected(): boolean {
    return (
      this.ability.can('read', PermissionsEnum.RmmPerEndpoint) &&
      this.ability.can('read', PermissionsEnum.RmmCeEnabled) &&
      this.form?.value?.selectedLicenseType === LicenseType.RmmCePerEndpoint
    );
  }

  setLicense(): void {
    this.confirmAssignWithoutAgent().then((proceed) => {
      if (proceed) this.performSetLicense();
    });
  }

  private confirmAssignWithoutAgent(): Promise<boolean> {
    const agentType = getAgentTypeForLicenseKind(this.kind);
    if (!agentType || Computer.hasAgent(this.computer, agentType)) {
      return Promise.resolve(true);
    }

    return this.modalService
      .openCustom(ConfirmAssignLicenseWithoutAgentModalComponent, {
        data: { computer: this.computer, licenseType: this.form.value.selectedLicenseType, agentType }
      })
      .then(() => true)
      .catch(() => false);
  }

  private performSetLicense(): void {
    const value = this.form.value;
    const params: SelectLicenseParams = {
      licenseTypeId: value.selectedLicenseType,
      hid: this.hid,
      availableLicenseId: this.rmmCESelected ? this.rmmCELicenseTypeId : value.selectedAnotherPoolLicense || value.selectedLicense
    };

    this.setLicenseLoading.set(true);
    this.licensesService.assignComputerLicense(params).subscribe({
      next: (result) => this.baseModal.save(result),
      error: (error) => {
        if (this.rmmCESelected && error?.error?.typeCode === ErrorCode.LicenseLimitExceeded) {
          this.showRMMPECEAlert.set(true);
          this.setLicenseLoading.set(false);
        } else {
          this.baseModal.close();
        }
      }
    });
  }

  public buyLicense(): void {
    const licenseTypeWillNotBeChanged = this.form.value.selectedLicenseType === this.computerLicenseByMode;

    if (this.baseModal.data?.isAgentSupportOfflineLicenseManagement || licenseTypeWillNotBeChanged) {
      const licenseType = this.form.value.selectedLicenseType;
      this.licensesFacade.proceedWithAutoAssign(licenseType, this.hid).then((isPurchased) => isPurchased && this.baseModal.save());
    } else {
      this.modalService
        .openCustom(BuyLicenseModalComponent, {
          data: {
            licenseType: this.form.value.selectedLicenseType,
            hid: this.hid
          }
        })
        .catch(noop);
    }
  }

  public isApplyBtnDisabled(isPostPayment: boolean) {
    return (
      isNil(this.form.value.selectedLicenseType) ||
      (isNil(this.form.value.selectedLicense) &&
        isNil(this.form.value.selectedAnotherPoolLicense) &&
        !isPostPayment &&
        this.form.value.changeLicenseCase !== ChangeLicenseCase.BUY)
    );
  }
}
